import { useSelector, useDispatch } from 'react-redux';
import { useState } from 'react';
import { setEventWindowData } from '../../Redux/store';
import DesktopWindow from '../../Menus/DesktopWindow';
import SplitView from '../../components/SplitView';
import EventPanel from './EventPanel';
import ContactPanel from './ContactPanel';
import useCalendarEvent from '../../hooks/useCalendarEvent';

/**
 * EventWindow - Desktop window for event/prediction viewing
 * Listens to eventWindowData Redux state
 * - { day, eventData } - event clicked, show existing event
 * - { day, predictionData } - prediction clicked, show event with contact from prediction
 * - { day } - day clicked, show new event
 */
function EventWindow() {
  const dispatch = useDispatch();
  // eventWindowData: Set in DayDesktop, contains { day, eventData, predictionData }
  const eventWindowData = useSelector((s) => s.ui.eventWindowData);
  const [creating, setCreating] = useState(false);
  const { createEvent, updateEvent } = useCalendarEvent();

  if (!eventWindowData) return null;

  // day: Date string (YYYY-MM-DD) for the calendar day being viewed
  // eventData: Full event object from calendarData, contains { id, date, title, contact, contactProfile, note, status }
  // predictionData: Prediction object from calendarData, contains { ownerName, ownerImages, ovulationActual, ovulationPrediction }
  const { day, eventData, predictionData } = eventWindowData;

  // contactId from eventData if it exists, if not from predictionData
  const contactId = eventData?.contact || predictionData?.ownerId;
  const eventId = eventData?.id;

  // Format date for title
  const formattedDate = new Date(day + 'T00:00').toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

  // Removes eventWindowData
  const handleClose = () => {
    dispatch(setEventWindowData(null));
  };

  // Creates event via useCalendarEvent hook which updates Redux and Supabase
  const handleCreateNew = async (newEventData) => {
    if (creating) return;
    setCreating(true);

    try {
      const realId = await createEvent(day, {
        title: '',
        subtitle: '',
        contact_id: contactId || null,
        status: 'pending',
        note: newEventData.note || ''
      });

      if (realId) {
        // Update eventWindowData with the new event data
        dispatch(setEventWindowData({
          day,
          eventData: { id: realId, date: day, title: '', subtitle: '', contact: contactId, status: 'pending', note: '' }
        }));
      }
    } catch (err) {
      console.error('[EventWindow] Failed to create event:', err);
    } finally {
      setCreating(false);
    }
  };

  // Removes contact via useCalendarEvent hook which updates Redux and Supabase
  const handleRemoveContact = async () => {
    if (!eventId) return;

    try {
      const success = await updateEvent(eventId, day, { contact: null });
      if (success) {
        // Update eventWindowData to remove contact from eventData
        dispatch(setEventWindowData({
          day,
          eventData: { ...eventData, contact: null }
        }));
      }
    } catch (err) {
      console.error('[EventWindow] Failed to remove contact:', err);
    }
  };

  // Adds contact via useCalendarEvent hook, creates event if needed with hook function
  const handleAddContact = async (newContactId) => {
    // If no event exists, create one with the contact
    if (!eventId) {
      if (creating) return;
      setCreating(true);

      try {
        const realId = await createEvent(day, {
          title: '',
          subtitle: '',
          contact_id: newContactId,
          status: 'pending',
          note: ''
        });

        if (realId) {
          // Update eventWindowData with the new event data
          dispatch(setEventWindowData({
            day,
            eventData: { id: realId, date: day, title: '', subtitle: '', contact: newContactId, status: 'pending', note: '' }
          }));
        }
      } catch (err) {
        console.error('[EventWindow] Failed to create event with contact:', err);
      } finally {
        setCreating(false);
      }
    } else {
      // Event exists, just update the contact
      try {
        const success = await updateEvent(eventId, day, { contact: newContactId });
        if (success) {
          // Update eventWindowData with the new contact in eventData
          dispatch(setEventWindowData({
            day,
            eventData: { ...eventData, contact: newContactId }
          }));
        }
      } catch (err) {
        console.error('[EventWindow] Failed to add contact:', err);
      }
    }
  };

  return (
    <DesktopWindow title={formattedDate} onClose={handleClose}>
      <SplitView>
        <EventPanel eventData={eventData} onClose={handleClose} onCreateNew={handleCreateNew} creating={creating} />
        <ContactPanel contactId={contactId} eventId={eventId} predictionData={predictionData} onRemoveContact={handleRemoveContact} onAddContact={handleAddContact} />
      </SplitView>
    </DesktopWindow>
  );
}

export default EventWindow;
